/**
 * VA-28159 — the full-screen hold.
 *
 * A candidate who leaves full screen puts the interview into a HOLD: a blocking
 * overlay asks them back, the clock freezes, and if the countdown runs out the
 * interview is terminated. Exactly the screen-share hold's shape, guarding a
 * different obligation, and sharing its implementation rather than copying it.
 *
 * WHY A HOLD AND NOT A BLOCK
 *
 * You cannot prevent a full-screen exit. Esc is a guaranteed escape hatch in
 * every browser and `preventDefault` does not reach it — that is a deliberate
 * security property, so that a page can never trap a user. The only control
 * available is to notice the exit and refuse to continue without it.
 *
 * WHY IT IS WORTH HAVING
 *
 * Proctoring's landmark overlay is drawn onto the screen recording, which means
 * the camera tile's position on the monitor has to be known. In full screen it
 * is exact: the viewport IS the screen, so the tile's rect is its screen
 * position. Outside full screen it depends on where the window sits, and the
 * browser has no dependable answer — `screenX`/`screenY` are allowed to be
 * obscured, and the CSSOM View issue asking for a reliable version of exactly
 * this (w3c/csswg-drafts#5814) is still open. Every measurement attempt against
 * live recordings landed the overlay a chrome-height out.
 *
 * Keeping the interview in full screen removes the question instead of
 * answering it: every captured frame is then taken in the one state where the
 * mapping is arithmetic rather than inference.
 *
 * THE FALSE POSITIVE TO RESPECT
 *
 * Full screen is lost involuntarily more often than it is abandoned. The
 * screen-share picker cancels it outright — which is why `enterFullscreen` runs
 * AFTER the picker, not before — and permission prompts and OS dialogs can do
 * the same. So the hold must not arm until the interview is actually running
 * and full screen has been established at least once; before that, not being
 * full screen is the normal state of a page that is still setting up.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useScreenShareHold, } from './screen-share-hold';
export const FULLSCREEN_MESSAGES = {
    stopped: 'fullscreen_exited',
    resumed: 'fullscreen_restored',
    terminate: 'fullscreen_terminate',
};
/** How long the candidate has to return before the interview ends.
 *
 * Matches the screen-share budget by decision — the two obligations are the
 * same kind of thing and a candidate should not have to learn two rules — and
 * is config-backed so an environment can be adjusted without a deploy.
 */
const DEFAULT_FULLSCREEN_HOLD_SECONDS = 30;
export const fullscreenHoldSeconds = () => {
    const raw = Number(process.env.REACT_APP_FULLSCREEN_HOLD_SECONDS);
    return Number.isFinite(raw) && raw > 0
        ? Math.floor(raw)
        : DEFAULT_FULLSCREEN_HOLD_SECONDS;
};
/**
 * Watches full screen and drives the hold.
 *
 * Polls as well as listening, deliberately. `fullscreenchange` covers only the
 * Fullscreen API; F11 native full screen fires `resize` instead, and some
 * window managers fire neither — so a control that trusted events alone would
 * miss the case where the candidate simply presses F11 to leave.
 */
export const useFullscreenHold = ({ send, onExpire, isSuspended, isArmed, isFullscreen, }) => {
    // Once the budget is spent this control is finished.
    //
    // The screen-share hold is entered once, by a track's `ended` handler. This
    // one is driven by a POLL, so without a latch the tick after expiry finds the
    // page still not full screen, calls `enterHold` again — which is permitted,
    // because expiry cleared the deadline — and starts a fresh countdown that
    // terminates all over again. The interview would be ended repeatedly and the
    // candidate would watch the timer reset instead of stopping.
    const expiredRef = useRef(false);
    const handleExpire = useCallback(() => {
        expiredRef.current = true;
        onExpire();
    }, [onExpire]);
    const hold = useScreenShareHold({
        send,
        onExpire: handleExpire,
        isSuspended,
        messages: FULLSCREEN_MESSAGES,
        holdSeconds: fullscreenHoldSeconds(),
    });
    const armedRef = useRef(isArmed);
    armedRef.current = isArmed;
    const fsRef = useRef(isFullscreen);
    fsRef.current = isFullscreen;
    const holdRef = useRef(hold);
    holdRef.current = hold;
    const sync = useCallback(() => {
        if (expiredRef.current)
            return;
        const full = fsRef.current();
        if (full) {
            // Leaving the hold is unconditional: if they are full screen, whatever we
            // were waiting for has happened, armed or not.
            holdRef.current.exitHold();
            return;
        }
        if (!armedRef.current())
            return;
        holdRef.current.enterHold();
    }, []);
    useEffect(() => {
        sync();
        document.addEventListener('fullscreenchange', sync);
        window.addEventListener('resize', sync);
        // Backstop for the paths that fire neither event. 500ms is fast enough that
        // the overlay never feels late and slow enough to cost nothing.
        const poll = window.setInterval(sync, 500);
        return () => {
            document.removeEventListener('fullscreenchange', sync);
            window.removeEventListener('resize', sync);
            window.clearInterval(poll);
        };
    }, [sync]);
    return hold;
};
/** True for BOTH the Fullscreen API and native/F11 full screen.
 *
 * F11 sets no `fullscreenElement`, so an API-only check calls a candidate who
 * is plainly full screen "not full screen" and holds them for it. The viewport
 * filling the screen is the property that actually matters to the mapping, and
 * it is the one being tested.
 */
export const isEffectivelyFullscreenNow = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined')
        return true;
    if (document.fullscreenElement)
        return true;
    const s = window.screen;
    if (!s || !s.width || !s.height)
        return false;
    // A small tolerance: some platforms leave a pixel or two, and a scrollbar can
    // shave the width without the page being any less full screen.
    return window.innerWidth >= s.width - 4 && window.innerHeight >= s.height - 4;
};
